
import { defineComponent, onMounted, ref, Transition } from "vue";
import ViewHeader from "@/components/ViewHeader.vue";
import { useI18n } from "vue-i18n";
import { useStore } from "@/store";
import { CloseShiftResponseModel, GroupedPaymentsModel } from "@/models/Shift";
import { api } from "@/services/Api";
import Swal from "sweetalert2";
import router from "@/router";
import { PaymentType } from "@/models/Enums";
import TextInput from "@/components/Inputs/TextInput.vue";
import OverlayModal from "./OverlayModal.vue";
import { Validation } from "@/models/Validation";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { Settings } from "@/models/Interfaces";
import { printPdf } from "@/utils/printer";
import useOrder from '@/modules/useOrder';
  
export default defineComponent({
    components: {
        ViewHeader,
        TextInput,
        OverlayModal,
        Transition
    },
    setup() {
        const { awaitTask } = useOrder();
        const { t } = useI18n();
        const store = useStore();
        const currencies = store.state.currencies;
        const user = store.state.user;
        const cashierId = ref(user?.cashierGuid);
        const initData = ref<CloseShiftResponseModel>();
        const isOpenIncorrectBalanceModal = ref(false);
        const isOpenClosingShiftModal = ref(false);
        const balanceDescirption = ref("");
        const managerCode = ref("");
        const settings = ref(store.getters.printerSettings as Settings);
        
        async function init(){
            Swal.showLoading();
            const response = await api.getCloseShift();

            if(response.error){
                await Swal.fire({
                    icon: "error",
                    title: response.errorMessage
                })
                .then(() => {
                    goHome();
                });
                return;                
            }

            if(response.data){
                initData.value = response.data;
            }     
            Swal.close();
        }

        function goHome(){
            router.push("/");
        }

        function getCurrencyName(currencyId: number): string | undefined {
            const currency = currencies.find(curr => curr.id === currencyId);
            return currency ? currency.name : undefined;
        }

        function getPaymentTypeName(paymentType: PaymentType): string {
            switch (paymentType) {
                case PaymentType.Cash:
                return t('cashier.label.cash');
                case PaymentType.Check:
                return t('cashier.label.check');
                case PaymentType.CreditCard:
                return t('cashier.label.creditcard');
                case PaymentType.BankTransfer:
                return t('cashier.label.banktransfer');
                default:
                return "Unknown";
            }
        }

        function getBalanceSummary(paymentType: PaymentType, currencyId: number, totalAmount: number){
            switch (paymentType) {
                case PaymentType.Cash:{
                    let balance = initData.value?.shiftOpenBalancesCash.find(ob => ob.currencyId == currencyId);
                    const roundedSum = Math.round(((balance?.amount ?? 0) + totalAmount) * 100) / 100;
                    return roundedSum;
                }                
                case PaymentType.Check:{
                    let balance = initData.value?.shiftOpenBalancesCheck.find(ob => ob.currencyId == currencyId);
                    const roundedSum = Math.round(((balance?.amount ?? 0) + totalAmount) * 100) / 100;
                    return roundedSum;
                }
                case PaymentType.CreditCard:
                case PaymentType.BankTransfer:
                    return totalAmount;
                default:
                    return 0;
            }
        }

        function getOpenBalance(paymentType: PaymentType, currencyId: number){
            switch (paymentType) {
                case PaymentType.Cash:{
                    let balance = initData.value?.shiftOpenBalancesCash.find(ob => ob.currencyId == currencyId);
                    return (balance?.amount ?? 0);
                }                
                case PaymentType.Check:{
                    let balance = initData.value?.shiftOpenBalancesCheck.find(ob => ob.currencyId == currencyId);
                    return (balance?.amount ?? 0);
                }
                default:
                    return 0;
            }
        }

        function getTotalAmount(groupedPayments: GroupedPaymentsModel[] | undefined) {
            let total = '';
            groupedPayments?.forEach((payment, index) => {
                total += `${payment.totalAmount} ${payment.currencyName} ${ index >= 0 && index + 1 != groupedPayments?.length ? ', ' : '' }` ;
            });
            return total;
        }

        onMounted(async () => {
            await init();
        });

        const rules = {
            required: {required}
        };

        const scope = "validationScope";
        const validation = new Validation(rules, scope);

        function cancelBalanceDesciption(){
            balanceDescirption.value = "";
            isOpenIncorrectBalanceModal.value = false;
        }

        const v = useVuelidate({ $scope: scope });

        async function closeShift(){
            if(`${user?.firstName} ${user?.lastName}` != initData.value?.openShiftUserFullName){
                await v.value.$validate();

                if(!managerCode.value){
                    isOpenClosingShiftModal.value = true;
                    return;
                }

                const swalResult = await Swal.fire({
                    icon: 'info',
                    title: t('cashier.shift.youSureToCloseShift', {openShiftUserFullName: initData.value?.openShiftUserFullName, startDateTime: initData.value?.startDateTime}),
                    customClass: {
                        confirmButton: 'my-1 px-5',
                        cancelButton: 'my-1 px-5'
                    },
                    showCancelButton: true,
                    confirmButtonText: t('alert.yes'),
                    cancelButtonText: t('alert.no')
                });
                if (!swalResult.isConfirmed) {
                    managerCode.value = "";
                    return;
                }

                isOpenClosingShiftModal.value = false;
            }

            Swal.showLoading();
            const response = await api.closeShift(settings.value.shiftPrinterSize, managerCode.value, balanceDescirption.value);
            managerCode.value = "";

            if(response.error){
                Swal.fire({
                    icon:"error",
                    title: response.errorMessage
                });
                return;
            }            

            const swalAction = await Swal.fire({
                icon: "question",
                text: t('cashier.shift.confirmationPrintMessageClose'),
                customClass: {
                    confirmButton: "popup-bookit-button",
                    cancelButton: "close-button-wrapper popup-bookit-button",
                },
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: t("cashier.label.yes"),
                cancelButtonText: t("cashier.label.no"),
            });

            if (swalAction.isConfirmed) {
                const shiftReportResponse = await api.generateShiftReport(settings.value.shiftPrinterSize, response.data!.shiftId, false);

                if(shiftReportResponse.error){
                    Swal.fire({
                        icon: "error",
                        text: shiftReportResponse.errorMessage,
                    });
                    return;
                }

                const taskId = shiftReportResponse.data;
                
                awaitTask(
                    () => api.waitExportTask(taskId!),
                        async (url) => {
                            await printPdf(url);
                            Swal.close();
                        }
                    );
            }else {
                Swal.fire({
                    icon: "success"
                });
                
            }

            goHome();
        }

        return {
            cashierId, 
            initData,
            goHome,
            getPaymentTypeName,
            getTotalAmount,
            getCurrencyName,
            isOpenIncorrectBalanceModal,
            cancelBalanceDesciption,
            balanceDescirption,
            managerCode,
            closeShift,
            getBalanceSummary,
            getOpenBalance,
            isOpenClosingShiftModal,
            scope,
            validation
        };
    },
});
